import Link from 'next/link'
import styled from 'styled-components'
import { lockPath } from '../config'
import { fmtDay, fmtShare, fmtSpan, fmtUsd } from '../format'
import { progressOf, sealToneOf } from '../logic/certificate'
import type { LockSummary } from '../types'
import { Icon } from '../../TokenPages/icons'
import TokenLogo from '../../TokenPages/components/TokenLogo'
import { DISPLAY, LockStatus, MONO } from '../ui'
import Seal from './Seal'

const Row = styled(Link)`
  display: grid;
  gap: 12px 14px;
  align-items: center;
  grid-template-columns: 44px minmax(0, 1fr) auto;
  grid-template-areas:
    'seal id status'
    'figs figs figs';
  padding: 14px;
  border-radius: 18px;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.text};
  background: linear-gradient(180deg, rgba(159, 182, 255, 0.05) 0%, transparent 40%), ${({ theme }) => theme.colors.backgroundAlt};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  box-shadow: inset 0 1px 0 rgba(223, 233, 255, 0.07);
  transition: border-color 0.15s ease, transform 0.15s ease;
  min-width: 0;
  &:hover {
    border-color: rgba(127, 212, 232, 0.55);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
  ${({ theme }) => theme.mediaQueries.lg} {
    padding: 12px 18px 12px 14px;
    grid-template-columns: 44px minmax(0, 2.3fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.25fr) 150px 20px;
    grid-template-areas: 'seal id value share unlock status chev';
  }
`
const Seal44 = styled.div`
  grid-area: seal;
  width: 44px;
  height: 44px;
`
const Identity = styled.div`
  grid-area: id;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`
const Logos = styled.div`
  display: flex;
  flex: none;
  > * + * {
    margin-left: -10px;
  }
`
const Title = styled.div`
  min-width: 0;
  display: grid;
  gap: 2px;
  b {
    font-family: ${DISPLAY};
    font-weight: 800;
    font-size: 17px;
    line-height: 22px;
    letter-spacing: -0.01em;
    overflow-wrap: anywhere;
  }
  span {
    font-size: 13px;
    line-height: 18px;
    color: ${({ theme }) => theme.colors.textSubtle};
    overflow-wrap: anywhere;
  }
`
const Figs = styled.div`
  grid-area: figs;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  ${({ theme }) => theme.mediaQueries.lg} {
    display: contents;
  }
`
const Cell = styled.div<{ $area: string; $dim?: boolean }>`
  display: grid;
  gap: 1px;
  min-width: 0;
  opacity: ${({ $dim }) => ($dim ? 0.6 : 1)};
  .l {
    font-size: 11.5px;
    line-height: 16px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
  .v {
    font-family: ${MONO};
    font-size: 15px;
    line-height: 22px;
    font-weight: 500;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
  }
  .s {
    font-size: 12.5px;
    line-height: 16px;
    color: ${({ theme }) => theme.colors.textSubtle};
    overflow-wrap: anywhere;
  }
  ${({ theme }) => theme.mediaQueries.lg} {
    grid-area: ${({ $area }) => $area};
    .l {
      display: none;
    }
  }
`
const Status = styled.div`
  grid-area: status;
  justify-self: end;
  ${({ theme }) => theme.mediaQueries.lg} {
    justify-self: stretch;
  }
`
const Chev = styled.span`
  display: none;
  grid-area: chev;
  color: ${({ theme }) => theme.colors.textSubtle};
  ${({ theme }) => theme.mediaQueries.lg} {
    display: inline-flex;
  }
`

const basisLabel = (l: LockSummary) => (l.shareBasis === 'token-supply' ? 'of supply' : l.shareBasis === 'lp-supply' ? 'of the LP' : l.shareBasis === 'active-liquidity' ? 'of active liquidity' : 'of the pool')

/** One lock in the explorer: its seal, who it is, and the three numbers a reader scans for (value, share, date). The whole row opens the certificate. */
export default function LockRow({ lock, now, readAt }: { lock: LockSummary; now: number | null; readAt: number }) {
  const protectedNow = lock.phase === 'locked' || lock.phase === 'vesting'
  const usd = protectedNow ? lock.lockedUsd : lock.valueUsd
  const share = protectedNow ? lock.lockedShareBps : lock.shareBps
  const next = lock.nextAt ?? lock.unlockAt
  const sub =
    lock.phase === 'released'
      ? 'withdrawn'
      : now === null
        ? ''
        : lock.phase === 'due'
          ? `passed ${fmtSpan(now - lock.unlockAt)} ago`
          : `in ${fmtSpan(next - now)}`
  const dim = lock.phase === 'released'
  return (
    <Row href={lockPath(lock.type, lock.id)} data-testid="lock-row" data-lock={`${lock.type}-${lock.id}`} data-phase={lock.phase} aria-label={`${lock.title}, ${lock.phase}`}>
      <Seal44>
        <Seal mini progress={progressOf(lock, now ?? readAt)} tone={sealToneOf(lock)} testid="row-seal" />
      </Seal44>
      <Identity>
        {lock.tokens.length > 0 ? (
          <Logos>
            {lock.tokens.slice(0, 2).map((t) => (
              <TokenLogo key={t.address} address={t.address} symbol={t.symbol} size={30} ring="soft" />
            ))}
          </Logos>
        ) : null}
        <Title>
          <b>{lock.title}</b>
          <span>
            {lock.type.toUpperCase()} #{lock.id} · {lock.subtitle}
          </span>
        </Title>
      </Identity>
      <Status>
        <LockStatus lock={lock} />
      </Status>
      <Figs>
        <Cell $area="value" $dim={dim}>
          <span className="l">{protectedNow ? 'Value locked' : 'Value held'}</span>
          <span className="v">{dim ? '—' : fmtUsd(usd)}</span>
          <span className="s">{lock.vesting && lock.phase !== 'released' ? 'still locked' : ' '}</span>
        </Cell>
        <Cell $area="share" $dim={dim}>
          <span className="l">Share</span>
          <span className="v">{dim ? '—' : fmtShare(share)}</span>
          <span className="s">{dim ? ' ' : basisLabel(lock)}</span>
        </Cell>
        <Cell $area="unlock" $dim={dim}>
          <span className="l">{lock.phase === 'vesting' ? 'Next release' : 'Unlocks'}</span>
          <span className="v">{fmtDay(lock.phase === 'released' ? lock.unlockAt : next)}</span>
          <span className="s">{sub || ' '}</span>
        </Cell>
      </Figs>
      <Chev>
        <Icon name="chevronRight" size={20} />
      </Chev>
    </Row>
  )
}
